import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sidebar } from "./AdminDashboard";
import { toast } from "sonner";
import { 
  DollarSign, 
  Calendar, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  CreditCard,
  Play,
  Loader2
} from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingManagement() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAdminAuthHeaders();
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/billing/summary`, { headers }),
        axios.get(`${API}/admin/billing/history`, { headers })
      ]);
      setSummary(summaryRes.data);
      setHistory(historyRes.data.billings);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const processWeeklyBilling = async () => {
    if (!confirm("Process weekly billing? This will charge all customers with outstanding fees and suspend licenses for failed payments.")) {
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/admin/billing/process-weekly`, {}, {
        headers: getAdminAuthHeaders()
      });
      const data = response.data;
      
      if (data.processed) {
        toast.success(`Billing processed: ${data.successful_charges} successful, ${data.failed_charges} failed`);
        if (data.failed_details?.length > 0) {
          toast.warning(`${data.failed_charges} licenses suspended due to payment failure`);
        }
      } else {
        toast.info(data.message);
      }
      
      fetchData();
    } catch (error) {
      toast.error("Failed to process billing");
    } finally {
      setProcessing(false);
    }
  };

  const reactivateLicense = async (licenseKey) => {
    try {
      await axios.post(`${API}/admin/license/${licenseKey}/reactivate`, {}, {
        headers: getAdminAuthHeaders()
      });
      toast.success("License reactivated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      paid: { class: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle className="w-3 h-3 mr-1" /> },
      pending: { class: 'bg-amber-100 text-amber-700', icon: <AlertTriangle className="w-3 h-3 mr-1" /> },
      failed: { class: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3 mr-1" /> },
      waived: { class: 'bg-slate-100 text-slate-700', icon: null }
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge className={`${style.class} flex items-center`}>
        {style.icon}
        {status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="billing-title">
              Weekly Billing
            </h1>
            <p className="text-slate-600">Manage 2% transaction fees from plugin owners</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={processWeeklyBilling}
              disabled={processing}
              className="bg-bronze hover:bg-amber-600 text-white"
              data-testid="process-billing-btn"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Process Weekly Billing
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Current Week Summary */}
            <Card className="mb-8" data-testid="current-week-summary">
              <CardHeader>
                <CardTitle className="font-outfit flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-bronze" />
                  Current Billing Period
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary?.billing_period && (
                  <p className="text-slate-600 mb-4">
                    {formatDate(summary.billing_period.start)} - {formatDate(summary.billing_period.end)}
                  </p>
                )}
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-100 rounded-lg p-4">
                    <p className="text-sm text-slate-600">Active Licenses with Transactions</p>
                    <p className="text-2xl font-bold text-midnight">{summary?.billing_data?.length || 0}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-sm text-emerald-700">Total Fees to Collect</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(summary?.total_fees_to_collect)}</p>
                  </div>
                </div>

                {summary?.billing_data?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-medium text-slate-600">License</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Customer</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Transactions</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Volume</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Fee (2%)</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.billing_data.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-3 px-4">
                              <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                                {item.license_key}
                              </code>
                            </td>
                            <td className="py-3 px-4">{item.customer_email}</td>
                            <td className="py-3 px-4">{item.total_transactions}</td>
                            <td className="py-3 px-4">{formatCurrency(item.total_volume)}</td>
                            <td className="py-3 px-4 font-semibold text-bronze">{formatCurrency(item.total_fees)}</td>
                            <td className="py-3 px-4">
                              <Badge className={item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                                {item.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No transactions this period</p>
                )}
              </CardContent>
            </Card>

            {/* Billing History */}
            <Card data-testid="billing-history">
              <CardHeader>
                <CardTitle className="font-outfit flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-bronze" />
                  Billing History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No billing history yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Period</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">License</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Customer</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Transactions</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Volume</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Fee</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                          <th className="text-left py-3 px-4 font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((billing, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-3 px-4 text-sm">
                              {formatDate(billing.billing_period_start)} - {formatDate(billing.billing_period_end)}
                            </td>
                            <td className="py-3 px-4">
                              <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                                {billing.license_key}
                              </code>
                            </td>
                            <td className="py-3 px-4">{billing.customer_email}</td>
                            <td className="py-3 px-4">{billing.total_transactions}</td>
                            <td className="py-3 px-4">{formatCurrency(billing.total_booking_volume)}</td>
                            <td className="py-3 px-4 font-semibold">{formatCurrency(billing.fee_amount)}</td>
                            <td className="py-3 px-4">
                              {getStatusBadge(billing.status)}
                              {billing.failure_reason && (
                                <p className="text-xs text-red-500 mt-1">{billing.failure_reason}</p>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {billing.status === 'failed' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => reactivateLicense(billing.license_key)}
                                  className="text-emerald-600 hover:text-emerald-700"
                                >
                                  Reactivate
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
